import { colors, createTheme, responsiveFontSizes } from "@mui/material";
import _ from "lodash";
import { THEMES } from "src/constants";
import { softShadows, strongShadows } from "./shadows";
import typography from "./typography";

const remoteTheme = window["theme"];

const newGray = "#848694";
const arkaduBlue = "#0F4C81";
const arkaduRed = "#FF1744";
const arkaduRedBg = "linear-gradient(180deg, #FF1744 30%, #E6294A 90%)";
const arkaduGray =
  "linear-gradient(180deg, rgba(37,37,37,1) 0%, rgba(26,34,32,1) 40%, rgba(17,18,18,1) 100%)";

const arkaduRedDark = "#E6294A";
const arkaduGrayPallete = {
  success: "#28a745",
  danger: "#E30613",
  warning: "#F6A000",
  info: "#018198",
  primary: {
    main: "#1F2B33",
    dark: "#172026",
    light: "#FFD500",
    secondary: "#018198",
  },
  secondary: {
    main: "#018198",
    dark: "#018198",
    light: "#FEDA00",
    dark2: "#DB8905",
  },
  primaryText: "#1F2B33",
  secondaryText: "#018198",
  background: {
    default: "#1F2B33",
    secondary: "#FEDA00",
    gray: "#FFFFFF",
  },
};

const arkaduBlueLight = "#4c77b1";
const textDark = "#2d2d2d";
const arkaduBlueDark = "#002554";
const secondaryColor = "#1AFF85";
const secondaryColorLight = "#00b28e";
const secondaryColorDark = "#64ffdf";
const newGreen = "#114430";
const unicornColor = "#64ffdf";

const barAdmin = secondaryColor;

const p = arkaduGrayPallete;

const baseOptions = {
  direction: "ltr",
  typography,
  shape: {
    borderRadius: 8,
  },
  transitions: {
    duration: {
      shortest: 150,
      shorter: 200,
      short: 250,
      standard: 300,
      complex: 375,
      enteringScreen: 225,
      leavingScreen: 195,
    },
    easing: {
      easeInOut: "cubic-bezier(0.4, 0, 0.2, 1)",
      easeOut: "cubic-bezier(0.0, 0, 0.2, 1)",
      easeIn: "cubic-bezier(0.4, 0, 1, 1)",
      sharp: "cubic-bezier(0.4, 0, 0.6, 1)",
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 500,
          borderRadius: 8,
          padding: "8px 20px",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          "&:hover": {
            transform: "translateY(-2px)",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
          },
          "&:active": {
            transform: "translateY(0)",
          },
        },
        contained: {
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: "0 2px 12px rgba(0, 0, 0, 0.08)",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          "&:hover": {
            boxShadow: "0 8px 24px rgba(0, 0, 0, 0.12)",
            transform: "translateY(-4px)",
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.06)",
          transition: "box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        },
        elevation1: {
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
        },
        elevation2: {
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
        },
      },
    },
    MuiTable: {
      styleOverrides: {
        root: {
          width: "100%",
          borderRadius: 8,
          overflow: "hidden",
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          "& .MuiTableCell-root": {
            fontWeight: 600,
            fontSize: "0.875rem",
            letterSpacing: "0.5px",
            textTransform: "uppercase",
          },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          transition: "background-color 0.2s ease",
          "&:hover": {
            backgroundColor: "rgba(0, 0, 0, 0.02)",
          },
          "&.Mui-selected": {
            backgroundColor: "rgba(1, 129, 152, 0.08)",
            "&:hover": {
              backgroundColor: "rgba(1, 129, 152, 0.12)",
            },
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: "1px solid rgba(224, 224, 224, 0.4)",
          padding: "16px",
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          borderBottom: "1px solid rgba(0, 0, 0, 0.08)",
          "& .MuiTabs-flexContainer": {
            gap: 8,
          },
        },
        indicator: {
          height: 3,
          borderRadius: "3px 3px 0 0",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 500,
          fontSize: "0.9375rem",
          minHeight: 48,
          transition: "all 0.2s ease",
          "&:hover": {
            color: p.secondary.main,
            opacity: 1,
          },
          "&.Mui-selected": {
            fontWeight: 600,
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: "#FFFFFF",
          color: p.secondary.main,
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: "#FFFFFF",
          borderRight: "1px solid rgba(0, 0, 0, 0.08)",
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            transition: "all 0.2s ease",
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: p.secondary.main,
            },
            "&.Mui-focused": {
              "& .MuiOutlinedInput-notchedOutline": {
                borderWidth: 2,
              },
            },
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
          transition: "all 0.2s ease",
          "&:hover": {
            transform: "scale(1.05)",
          },
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          margin: "2px 8px",
          transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
          "&:hover": {
            backgroundColor: "rgba(1, 129, 152, 0.08)",
            transform: "translateX(4px)",
          },
          "&.Mui-selected": {
            backgroundColor: "rgba(1, 129, 152, 0.12)",
            "&:hover": {
              backgroundColor: "rgba(1, 129, 152, 0.16)",
            },
          },
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: 40,
          transition: "color 0.2s ease",
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          padding: "10px 16px",
          transition: "background-color 0.2s ease",
          "&:hover": {
            backgroundColor: "rgba(1, 129, 152, 0.08)",
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          transition: "all 0.2s ease",
          "&:hover": {
            backgroundColor: "rgba(0, 0, 0, 0.04)",
            transform: "scale(1.1)",
          },
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          overflow: "hidden",
          height: 6,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 12,
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontSize: "1.25rem",
          fontWeight: 600,
        },
      },
    },
    MUIDataTableFilter: {
      styleOverrides: {
        root: {
          backgroundColor: "rgba(232, 232, 232, 0.5)",
          borderRadius: 8,
        },
      },
    },
  },
};

const themesOptions = [
  {
    name: THEMES.LIGHT,
    palette: {
      mode: "light",
      action: {
        active: p.primary.light,
      },
      background: {
        ...p.background,
        dark: "#FAFAFA",
        paper: colors.common.white,
      },
      primary: p.primary,
      secondary: p.secondary,
      danger: {
        main: p.danger,
      },
      text: {
        primary: p.primaryText,
        secondary: p.secondaryText,
      },
      success: {
        main: p.success,
      },
      adminBar: {
        main: p.secondary,
      },
    },
    shape: {
      borderRadius: 8,
    },
    shadows: softShadows,
  },
  {
    name: THEMES.ONE_DARK,
    palette: {
      mode: "dark",
      action: {
        active: "rgba(255, 255, 255, 0.54)",
        hover: "rgba(255, 255, 255, 0.08)",
        selected: "rgba(255, 255, 255, 0.16)",
        disabled: "rgba(255, 255, 255, 0.26)",
        disabledBackground: "rgba(255, 255, 255, 0.12)",
        focus: "rgba(255, 255, 255, 0.12)",
      },
      background: {
        default: "#282C34",
        dark: "#1c2025",
        paper: "#2C323D",
      },
      primary: {
        main: "#8a85ff",
      },
      success: {
        main: p.success,
      },
      danger: {
        main: p.danger,
      },
      secondary: {
        main: "#8a85ff",
      },
      text: {
        primary: "rgba(255, 255, 255, 0.95)",
        secondary: "rgba(255, 255, 255, 0.7)",
        disabled: "rgba(255, 255, 255, 0.38)",
      },
      adminBar: {
        main: p.secondary,
      },
    },
    shape: {
      borderRadius: 8,
    },
    shadows: strongShadows,
  },
  // {
  //   name: THEMES.UNICORN,
  //   palette: {
  //     mode: "dark",
  //     action: {
  //       active: "rgba(255, 255, 255, 0.54)",
  //       hover: "rgba(255, 255, 255, 0.04)",
  //       selected: "rgba(255, 255, 255, 0.08)",
  //       disabled: "rgba(255, 255, 255, 0.26)",
  //       disabledBackground: "rgba(255, 255, 255, 0.12)",
  //       focus: "rgba(255, 255, 255, 0.12)",
  //     },
  //     background: {
  //       default: "#2a2d3d",
  //       dark: "#222431",
  //       paper: "#2a2d3d",
  //     },
  //     primary: {
  //       main: unicornColor,
  //     },
  //     danger: {
  //       main: p.danger,
  //     },
  //     success: {
  //       main: p.success,
  //     },
  //     secondary: {
  //       main: unicornColor,
  //     },
  //     text: {
  //       primary: "#f6f5f8 ",
  //       secondary: "#9699a4",
  //     },
  //     adminBar: {
  //       main: p.secondary,
  //     },
  //   },
  //   shape: {
  //     borderRadius: 12,
  //   },
  //   shadows: strongShadows,
  // },
];

export const buildTheme = (config = {}, customerTheme = {}) => {
  let themeOptions = themesOptions.find((theme) => theme.name === config.theme);

  if (!themeOptions) {
    themeOptions = themesOptions.find((theme) => theme.name === "LIGHT");
    [themeOptions] = themesOptions;
  }

  if (customerTheme.primary) {
    themeOptions = _.merge(themeOptions, { palette: customerTheme });
  }
  themeOptions = _.merge(themeOptions, baseOptions);
  themeOptions = _.merge(themeOptions, remoteTheme);

  var themeConfig = {
    // ...baseOptions,
    ...themeOptions,

    direction: config.direction,
  };
  let theme = createTheme(themeConfig);

  if (config.responsiveFontSizes) {
    theme = responsiveFontSizes(theme);
  }

  return theme;
};
